import React, { useState, useMemo } from 'react';
import { Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShieldAlt, faUser } from '@fortawesome/free-solid-svg-icons';
import { getInitials } from '../utils/helpers';
import MultiSelectDropdown from '../../../globalComponents/form/dropdowns/MultiSelectDropdown';

/**
 * UsersList
 *
 * Displays all company users and lets the user navigate to per-user
 * permission management. Includes an "Add User" button that opens the
 * AddUserModal component.
 *
 
 */
const UsersList = ({
  users,
  isLoading,
  onSelectUser,
  roles = [],
  onRefetch,
  selectedRoleFilter = [],
  onRoleFilterChange = () => {}
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return (users || []).filter(u => {
      const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ');
      return (
        !u.head_office &&
        (fullName.toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s))
      );
    });
  }, [users, search]);

  const roleOptions = useMemo(() => (roles || []).map(r => ({ value: r.id, label: r.name })), [roles]);

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="users-list-tab">

      {/* Header */}
      <div className="auth-tab-header">
        <div>
          <h4>Users</h4>
          <p className="text-muted mb-0">{users?.length ?? 0} total users</p>
        </div>
      </div>

      {/* Search & Role Filter */}
      <div className="auth-search-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%', marginBottom: '24px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <Form.Control
            type="text"
            placeholder="Search users by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="auth-search-input"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ width: 220 }}>
          <MultiSelectDropdown
            options={roleOptions}
            selected={selectedRoleFilter}
            onChange={onRoleFilterChange}
            placeholder="All Roles"
            searchPlaceholder="Search roles..."
            showSelectAll={true}
          />
        </div>
      </div>

      {/* User Cards */}
      <div className="users-grid">
        {filtered.map(user => {
          const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
          return (
            <div key={user.id} className="user-card">
              <div className="user-card-left">
                <div className="user-avatar">
                  {getInitials(fullName || user.email)}
                </div>
                <div className="user-info">
                  <div className="user-name">{fullName || 'Unknown'}</div>
                  <div className="user-email">{user.email}</div>
                </div>
              </div>
              <button className="auth-btn-manage" onClick={() => onSelectUser(user)}>
                <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                Manage Permissions
              </button>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="auth-empty">
            <FontAwesomeIcon icon={faUser} className="auth-empty-icon" />
            <p>No users found</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default UsersList;